import { useState, useEffect, FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Briefcase, CheckCircle, Loader2, MapPin, Building2, Copy } from 'lucide-react';
import { GGJLogoMark } from '@shared/components/Brand/GGJLogo';
import { careerService, type CareerJobRow } from '@core/supabase/services/careerService';

/** §8.14 E2E — published jobs + apply → candidate + application in ATS (RPC `ggj_b2b_career_apply`). */
export function CareersOrgPage() {
  const { orgSlug: orgSlugParam } = useParams<{ orgSlug: string }>();
  const orgSlug = orgSlugParam ? decodeURIComponent(orgSlugParam) : '';

  const [orgName, setOrgName] = useState<string | null>(null);
  const [jobs, setJobs] = useState<CareerJobRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [applyJob, setApplyJob] = useState<CareerJobRow | null>(null);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [cover, setCover] = useState('');
  const [resumeUrl, setResumeUrl] = useState('');
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [doneMessage, setDoneMessage] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgSlug) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    Promise.all([careerService.getOrgPublic(orgSlug), careerService.listPublishedJobs(orgSlug)])
      .then(([meta, list]) => {
        if (cancelled) return;
        if (!meta) {
          setOrgName(null);
          setJobs([]);
          setLoadError('We could not find that organisation. Check the slug or ask your recruiter for the link.');
          return;
        }
        setOrgName(meta.org_name);
        setJobs(list);
      })
      .catch((e: Error) => {
        if (!cancelled) setLoadError(e.message || 'Failed to load careers.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgSlug]);

  const openApply = (job: CareerJobRow) => {
    setApplyJob(job);
    setDoneMessage(null);
    setFormError(null);
    setResumeFile(null);
  };

  const closeApply = () => {
    setApplyJob(null);
    setSubmitting(false);
    setFormError(null);
    setResumeFile(null);
  };

  const onApply = async (e: FormEvent) => {
    e.preventDefault();
    if (!applyJob || !orgSlug) return;
    setFormError(null);
    setSubmitting(true);
    try {
      let resumeStoragePath: string | undefined;
      if (resumeFile) {
        try {
          resumeStoragePath = await careerService.uploadResumeForCareerApply(orgSlug, applyJob.id, resumeFile);
        } catch (upErr: unknown) {
          setFormError(upErr instanceof Error ? upErr.message : 'Resume upload failed.');
          return;
        }
      }

      const res = await careerService.apply({
        orgSlug,
        jobId: applyJob.id,
        fullName,
        email,
        phone: phone || undefined,
        coverText: cover || undefined,
        resumeUrl: resumeFile ? undefined : resumeUrl || undefined,
        resumeStoragePath,
      });
      if (!res.ok) {
        setFormError(
          res.error === 'invalid_input'
            ? 'Please enter your full name and a valid email.'
            : res.error === 'job_not_found'
              ? 'This role is no longer accepting applications.'
              : res.error === 'invalid_resume_path'
                ? 'Resume could not be attached. Try again or use a resume link instead.'
                : res.detail || res.error || 'Something went wrong.'
        );
        return;
      }

      const trackUrl =
        res.track_token && typeof window !== 'undefined'
          ? `${window.location.origin}/careers/status/${encodeURIComponent(res.track_token)}`
          : null;

      if (res.ok && res.application_id && email && !res.duplicate) {
        void careerService.requestCareerConfirmationEmail(res.application_id, email).catch(() => {
          /* optional: email requires deployed edge + RESEND_API_KEY */
        });
      }

      setDoneMessage(
        res.duplicate
          ? `You already applied for this role. The hiring team has your application.${
              trackUrl ? ` Track status: ${trackUrl}` : ''
            }`
          : `Application received. The hiring team will review your profile.${
              trackUrl
                ? ` Check your email for confirmation, or track status: ${trackUrl}`
                : ''
            }`
      );
      setFullName('');
      setEmail('');
      setPhone('');
      setCover('');
      setResumeUrl('');
      setResumeFile(null);
      setApplyJob(null);
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Submit failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex flex-col bg-gray-950 text-gray-100">
      <header className="border-b border-gray-800/80 px-4 py-4 sm:px-8">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <GGJLogoMark size={36} className="shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white tracking-tight truncate">
                {orgName ?? 'Careers'}
              </p>
              <p className="text-[11px] text-gray-500 truncate">Open roles · {orgSlug}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {orgName && orgSlug && (
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-md border border-gray-700 px-2 py-1 text-[11px] text-gray-400 hover:border-gray-600 hover:text-white"
                title="Copy public jobs page URL"
                onClick={() => {
                  const u = `${window.location.origin}/careers/${encodeURIComponent(orgSlug)}`;
                  void navigator.clipboard.writeText(u);
                }}
              >
                <Copy className="w-3 h-3" /> Copy URL
              </button>
            )}
            <Link to="/careers" className="text-xs text-gray-400 hover:text-white hidden sm:inline">
              Other companies
            </Link>
            <Link to="/" className="text-xs text-brand-400 hover:text-brand-300 font-medium">
              Recruiter sign in →
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-8 sm:px-8">
        <div className="max-w-3xl mx-auto">
          {doneMessage && (
            <div className="mb-6 rounded-lg border border-green-800/60 bg-green-950/30 px-4 py-3 flex items-start gap-3 text-sm text-green-100">
              <CheckCircle className="w-5 h-5 shrink-0 text-green-400 mt-0.5" />
              <span>{doneMessage}</span>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center gap-2 py-16 text-gray-400 text-sm">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading roles…
            </div>
          )}

          {!loading && loadError && (
            <div className="rounded-lg border border-amber-800/50 bg-amber-950/20 px-4 py-3 text-sm text-amber-100">{loadError}</div>
          )}

          {!loading && !loadError && orgName && jobs.length === 0 && (
            <div className="rounded-xl border border-dashed border-gray-700 bg-gray-900/40 p-10 text-center">
              <Briefcase className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-400">No published openings right now.</p>
              <p className="text-xs text-gray-600 mt-2">Roles appear here when the team sets a job to <strong className="text-gray-400">Published</strong>.</p>
            </div>
          )}

          {!loading && !loadError && jobs.length > 0 && (
            <ul className="space-y-3">
              {jobs.map((job) => (
                <li
                  key={job.id}
                  className="rounded-xl border border-gray-800 bg-gray-900/50 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4"
                >
                  <div className="min-w-0">
                    <h2 className="text-base font-semibold text-white">{job.title}</h2>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1">
                        <Building2 className="w-3.5 h-3.5" />
                        {job.client_name}
                      </span>
                      {job.location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {job.location}
                        </span>
                      )}
                    </div>
                    {job.career_seo_summary?.trim() ? (
                      <p className="text-sm text-gray-300 mt-3 line-clamp-5 whitespace-pre-wrap">{job.career_seo_summary.trim()}</p>
                    ) : job.description ? (
                      <p className="text-sm text-gray-400 mt-3 line-clamp-4 whitespace-pre-wrap">{job.description}</p>
                    ) : null}
                    {job.skills_required && job.skills_required.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {job.skills_required.slice(0, 8).map((s) => (
                          <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-800 text-gray-300">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => openApply(job)}
                    className="shrink-0 rounded-lg bg-brand-600 hover:bg-brand-500 text-white text-sm font-medium px-4 py-2.5 w-full sm:w-auto"
                  >
                    Apply
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>

      {applyJob && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-4" role="dialog" aria-modal>
          <div className="w-full max-w-md rounded-xl border border-gray-700 bg-gray-900 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-800">
              <h3 className="text-lg font-semibold text-white">Apply · {applyJob.title}</h3>
              <p className="text-xs text-gray-500 mt-1">{orgName}</p>
            </div>
            <form onSubmit={onApply} className="p-4 space-y-3">
              {formError && <p className="text-xs text-red-400">{formError}</p>}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Full name</label>
                <input
                  required
                  className="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-white"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Email</label>
                <input
                  required
                  type="email"
                  className="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-white"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Phone (optional)</label>
                <input
                  className="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-white"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Resume file (optional, PDF or Word, max 5MB)</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="w-full text-xs text-gray-300 file:mr-2 file:rounded file:border-0 file:bg-gray-800 file:px-2 file:py-1"
                  onChange={(e) => {
                    setResumeFile(e.target.files?.[0] ?? null);
                    if (e.target.files?.[0]) setResumeUrl('');
                  }}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Or resume link (optional)</label>
                <input
                  type="url"
                  placeholder="https://…"
                  disabled={!!resumeFile}
                  className="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-white disabled:opacity-50"
                  value={resumeUrl}
                  onChange={(e) => setResumeUrl(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Cover note (optional)</label>
                <textarea
                  rows={3}
                  className="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-white resize-y min-h-[72px]"
                  value={cover}
                  onChange={(e) => setCover(e.target.value)}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={closeApply} className="flex-1 btn-secondary py-2 text-sm">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 rounded-lg bg-brand-600 hover:bg-brand-500 disabled:opacity-60 text-white text-sm font-medium py-2"
                >
                  {submitting ? 'Submitting…' : 'Submit application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
